import { ReactNode, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Icon } from '@factorialco/factorial-one-react'
import { Handle, LockLocked } from '@factorialco/factorial-one-react/icons/app'
import { FormsElementTypeEnum } from 'generated/resources/graphql'

import { Element } from 'modules/forms/lib/Element'
import { Form } from 'modules/forms/lib/Form'

import { SectionElement } from '../../lib/elements/SectionElement'
import { QuestionSidebarDropdown, SectionSidebarDropdown } from '../dropdowns'
import DeleteDialog from '../modals/DeleteDialog'

export const DRAG_TYPE = 'SIDEBAR_ELEMENT'

// Info about the element being dragged
export interface DragItemInfo {
  id: string //Element id
  isSection: boolean
  fromIndex: number //original position
  fromSectionId: string | null //original section id
  toIndex: number //index to go when dropped
  toSectionId: string | null //section to go when dropped
}

interface Props {
  form: Form
  selected: boolean
  onSelect: () => void
  index: number
  element: Element

  // The function to change the order while dragging
  // to preview de dropped position
  onHover: (elementId: string, toSectionId: string | null, toIndex: number) => void

  // The function to update the elements when finish dragging
  onDrop: (elementId: string, toPosition: number, toSectionId: string | null) => void

  children?: ReactNode
  onAddElement: (type: FormsElementTypeEnum, sectionId: string | null) => void
  onDuplicateElement: (element: Element) => void
}

const SidebarElement = ({
  element,
  selected,
  onSelect,
  index,
  form,
  onHover,
  onDrop,
  children,
  onAddElement,
  onDuplicateElement,
}: Props) => {
  const [hovered, setHover] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DRAG_TYPE,
      item: (): DragItemInfo | null => {
        // Canit drag if element is locked
        if (element.isLocked()) {
          return null
        }
        return {
          id: element.id,
          fromIndex: index,
          fromSectionId: element.sectionId,
          toIndex: index,
          toSectionId: element.sectionId,
          isSection: element instanceof SectionElement,
        }
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item: DragItemInfo, monitor) => {
        if (!monitor.didDrop()) {
          // if dragging has been canceled .... put the dragging element back in the initial position
          onHover(item.id, item.fromSectionId, item.fromIndex)
        }
      },
    }),
    [element, element.sectionId, index]
  )
  const [, drop] = useDrop(
    {
      accept: DRAG_TYPE,
      hover(item: DragItemInfo) {
        // Can't drop on a locked element
        if (element.isLocked()) return
        // It is not possible to swap an element if it is the element itself
        if (item.id === element.id) return

        // It is not possible to swap an element if dragging a section inside a section
        if (item.isSection && element.sectionId) return

        if (item.toIndex === index && item.toSectionId === element.sectionId) return
        onHover(item.id, element.sectionId, index)
        item.toIndex = index
        item.toSectionId = element.sectionId
      },
      drop(item: DragItemInfo) {
        //do nothing if dropped in the original position
        if (item.fromIndex === item.toIndex && item.fromSectionId === item.toSectionId) return
        onDrop(item.id, item.toIndex, item.toSectionId)
      },
      canDrop(item: DragItemInfo) {
        // Can't drop on a locked element
        if (element.isLocked()) return false
        //it's not possible to drop a section inside another section
        return !(item.isSection && element.sectionId)
      },
    },
    [element, index]
  )

  const onAddElementSection = async (type: FormsElementTypeEnum, sectionId: string | null) => {
    setHover(false)
    onAddElement(type, sectionId)
  }

  return (
    <>
      <>
        <Box
          ref={(node) => drag(drop(node))}
          onClick={onSelect}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          flexDirection="row"
          className={`transition-colors px-3 py-2 rounded cursor-pointer ${
            selected ? 'bg-f1-background-selected' : 'hover:bg-f1-background-hover'
          }`}
          style={{ opacity: isDragging ? '0.5' : '1' }}
          gap="s8"
          justifyContent="spaceBetween"
          alignItems="center"
        >
          <Box flexDirection="row" gap="s8" alignItems="center">
            {!element.isLocked() && (
              <Icon
                style={{ visibility: hovered ? 'visible' : 'hidden' }}
                icon={Handle}
                className="text-f1-icon"
                size="sm"
              />
            )}
            <Icon icon={element.getIcon()} className="text-f1-icon" />
            <Text.Normal lineClamp={2}>{element.label}</Text.Normal>
            {element.mandatory && (
              <Text size="200" weight="medium" color="accentBackgroundSolid">
                {'*'}
              </Text>
            )}
          </Box>
          {element.isLocked() ? (
            <Icon icon={LockLocked} className="text-f1-icon" size="sm" />
          ) : element.getType() === 'section' ? (
            <SectionSidebarDropdown
              isVisible={hovered}
              sectionId={element.id ?? undefined}
              onDelete={() => setOpenDialog(true)}
              onAddElement={onAddElementSection}
            />
          ) : (
            <QuestionSidebarDropdown
              isVisible={hovered}
              onDelete={() => setOpenDialog(true)}
              onDuplicate={() => onDuplicateElement(element)}
            />
          )}
        </Box>
        {children}
      </>
      <DeleteDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        formId={form.id}
        elementId={element.id}
        resourceId={form.resourceId}
        resourceType={form.resourceType}
      />
    </>
  )
}

export default SidebarElement
